import React, { useMemo, useState } from "react";
import { useToast } from "./Toast";
import {
  MENU_INTRO,
  INCLUIDO,
  COMIDA_ADULTOS,
  COMIDA_INFANTIL,
  BARRAS,
} from "../data/content";
import { isValidName, isValidPhone, isValidEmail, sanitizePhone } from "../utils/validation";

function validateQuote({ nombre, telefono, correo, adultos }) {
  const errors = {};
  if (!isValidName(nombre)) {
    errors.nombre = "Escribe tu nombre completo";
  }
  if (!isValidPhone(telefono)) {
    errors.telefono = "Escribe solo números (10 a 15 dígitos)";
  }
  if (!isValidEmail(correo)) {
    errors.correo = "Escribe un correo válido";
  }
  if (!adultos || Number(adultos) < 1) {
    errors.adultos = "Indica cuántos adultos asistirán";
  }
  return errors;
}

const TABS = [
  { key: "incluido", label: "Todo Incluido", c: "fucsia" },
  { key: "adultos", label: "Menú Adultos", c: "turquesa" },
  { key: "infantil", label: "Menú Infantil", c: "naranja" },
  { key: "barras", label: "Barras Gourmet", c: "lima" },
  { key: "cotiza", label: "Tarifas", c: "morado" },
];

export default function Menu() {
  const toast = useToast();
  const [tab, setTab] = useState("incluido");

  const [adultos, setAdultos] = useState("");
  const [ninos, setNinos] = useState("");
  const [nombre, setNombre] = useState("");
  const [telefono, setTelefono] = useState("");
  const [correo, setCorreo] = useState("");
  const [touched, setTouched] = useState({});
  const [sending, setSending] = useState(false);

  const quoteErrors = useMemo(
    () => validateQuote({ nombre, telefono, correo, adultos }),
    [nombre, telefono, correo, adultos]
  );
  const canSubmitQuote = Object.keys(quoteErrors).length === 0;
  const showQuoteError = (name) => touched[name] && quoteErrors[name];
  const markTouched = (name) => setTouched((prev) => ({ ...prev, [name]: true }));

  const onTelefonoChange = (e) => {
    setTelefono(sanitizePhone(e.target.value));
    markTouched("telefono");
  };

  const onQuoteSubmit = async (e) => {
    e.preventDefault();
    if (!canSubmitQuote) {
      setTouched({ nombre: true, telefono: true, correo: true, adultos: true });
      toast("Revisa los campos marcados en rojo ✿");
      return;
    }
    setSending(true);
    try {
      const res = await fetch("/api/leads", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          origen: "cotizacion-menu",
          nombre,
          telefono,
          correo,
          adultos,
          ninos: ninos ? Number(ninos) : 0,
        }),
      });
      if (!res.ok) throw new Error("bad status");
      toast("✿ ¡Solicitud enviada! Un asesor te contacta con tu cotización a la medida ✿");
      setAdultos("");
      setNinos("");
      setNombre("");
      setTelefono("");
      setCorreo("");
      setTouched({});
    } catch {
      toast("No pudimos enviar tu solicitud, intenta de nuevo en unos minutos ✿");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="menu" className="band sage" data-screen-label="04b Menu">
      <div className="band-bg" />
      <div className="scatter" />
      <div className="tear-top tear-coral-top" />
      <div className="tear-bottom tear-sage-bottom" />

      <div className="wrap">
        <div className="sec-head">
          <h2 className="sec-title">Menú</h2>
          <div className="sec-kicker">{MENU_INTRO}</div>
        </div>

        <div className="g-tabs">
          {TABS.map((t) => (
            <button
              key={t.key}
              className={"gtab c-" + t.c + (tab === t.key ? " active" : "")}
              onClick={() => setTab(t.key)}
            >
              {t.label}
            </button>
          ))}
        </div>

        <div className="menu-panel" key={tab}>
          {tab === "incluido" && (
            <div className="check-grid">
              {INCLUIDO.map((item) => (
                <div className="check-item" key={item}>
                  <span className="c-ico">✓</span>
                  {item}
                </div>
              ))}
            </div>
          )}

          {tab === "adultos" && (
            <div className="menu-grid">
              {COMIDA_ADULTOS.map((c) => (
                <div className="menu-card" key={c.title}>
                  <h4>{c.title}</h4>
                  <p>{c.text}</p>
                  {c.list && (
                    <ul>
                      {c.list.map((li) => (
                        <li key={li}>{li}</li>
                      ))}
                    </ul>
                  )}
                  {c.incluye && (
                    <p className="incluye">
                      <b>Incluye:</b> {c.incluye}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "infantil" && (
            <div className="menu-grid cols-2">
              {COMIDA_INFANTIL.map((c) => (
                <div className="menu-card" key={c.title}>
                  <h4>{c.title}</h4>
                  <p>{c.text}</p>
                  {c.incluye && (
                    <p className="incluye">
                      <b>Incluye:</b> {c.incluye}
                    </p>
                  )}
                </div>
              ))}
            </div>
          )}

          {tab === "barras" && (
            <div className="menu-grid cols-2">
              {BARRAS.map((b) => (
                <div className="menu-card" key={b.title}>
                  <span className="note">{b.note}</span>
                  <h4>{b.title}</h4>
                  <p>{b.text}</p>
                  <ul>
                    {b.list.map((li) => (
                      <li key={li}>{li}</li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>
          )}

          {tab === "cotiza" && (
            <div className="quote-wrap">
              <div className="menu-card quote-card">
                <span className="note">sin compromiso</span>
                <h4>Cotización a tu medida</h4>
                <p>
                  El costo depende del menú, la temática y el número de invitados —
                  cuéntanos cuántos son y te contactamos con una propuesta personalizada.
                </p>
                <p className="note">Pedido mínimo de 50 platillos por evento.</p>
                <form onSubmit={onQuoteSubmit} noValidate>
                  <div className="row2c">
                    <div>
                      <label className="field">Adultos</label>
                      <input
                        className={"input" + (showQuoteError("adultos") ? " has-error" : "")}
                        type="number"
                        min="0"
                        placeholder="Ej. 20"
                        value={adultos}
                        onChange={(e) => {
                          setAdultos(e.target.value);
                          markTouched("adultos");
                        }}
                      />
                      {showQuoteError("adultos") && (
                        <span className="field-error">{quoteErrors.adultos}</span>
                      )}
                    </div>
                    <div>
                      <label className="field">Niños</label>
                      <input
                        className="input"
                        type="number"
                        min="0"
                        placeholder="Ej. 15 (opcional)"
                        value={ninos}
                        onChange={(e) => setNinos(e.target.value)}
                      />
                    </div>
                  </div>
                  <div className="row2c">
                    <div>
                      <label className="field">Nombre</label>
                      <input
                        className={"input" + (showQuoteError("nombre") ? " has-error" : "")}
                        placeholder="María González"
                        value={nombre}
                        onChange={(e) => {
                          setNombre(e.target.value);
                          markTouched("nombre");
                        }}
                      />
                      {showQuoteError("nombre") && (
                        <span className="field-error">{quoteErrors.nombre}</span>
                      )}
                    </div>
                    <div>
                      <label className="field">Teléfono / WhatsApp</label>
                      <input
                        className={"input" + (showQuoteError("telefono") ? " has-error" : "")}
                        type="tel"
                        inputMode="numeric"
                        placeholder="5610089073"
                        value={telefono}
                        onChange={onTelefonoChange}
                      />
                      {showQuoteError("telefono") && (
                        <span className="field-error">{quoteErrors.telefono}</span>
                      )}
                    </div>
                  </div>
                  <label className="field">Correo</label>
                  <input
                    className={"input" + (showQuoteError("correo") ? " has-error" : "")}
                    type="email"
                    placeholder="maria@correo.mx"
                    value={correo}
                    onChange={(e) => {
                      setCorreo(e.target.value);
                      markTouched("correo");
                    }}
                  />
                  {showQuoteError("correo") && (
                    <span className="field-error">{quoteErrors.correo}</span>
                  )}
                  <button className="btn" type="submit" style={{ marginTop: 14 }} disabled={!canSubmitQuote || sending}>
                    {sending ? (
                      <>
                        <span className="btn-spinner" /> Reservando…
                      </>
                    ) : (
                      "Solicitar cotización"
                    )}
                  </button>
                </form>
              </div>
            </div>
          )}
        </div>
      </div>
    </section>
  );
}
